import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  collection, doc, setDoc, deleteDoc, onSnapshot, query, serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './AuthContext';

const MovieContext = createContext();

export const MovieProvider = ({ children }) => {
  const { user } = useAuth();
  const [watchlist, setWatchlist]       = useState([]);
  const [searchQuery, setSearchQuery]   = useState('');
  const [selectedGenre, setSelectedGenre] = useState(null);

  // ── Firestore real-time watchlist sync ──────────────────────────
  useEffect(() => {
    if (!user) {
      // Fall back to localStorage for guests
      try { setWatchlist(JSON.parse(localStorage.getItem('cinespur_watchlist') || '[]')); }
      catch { setWatchlist([]); }
      return;
    }
    const q = query(collection(db, 'users', user.uid, 'watchlist'));
    const unsub = onSnapshot(q, (snap) => {
      setWatchlist(snap.docs.map(d => d.data()));
    });
    return unsub;
  }, [user]);

  const addToWatchlist = useCallback(async (movie) => {
    if (user) {
      const ref = doc(db, 'users', user.uid, 'watchlist', String(movie.id));
      await setDoc(ref, { ...movie, savedAt: serverTimestamp() });
    } else {
      setWatchlist(prev => {
        if (prev.find(m => m.id === movie.id)) return prev;
        const updated = [...prev, movie];
        localStorage.setItem('cinespur_watchlist', JSON.stringify(updated));
        return updated;
      });
    }
  }, [user]);

  const removeFromWatchlist = useCallback(async (id) => {
    if (user) {
      await deleteDoc(doc(db, 'users', user.uid, 'watchlist', String(id)));
    } else {
      setWatchlist(prev => {
        const updated = prev.filter(m => m.id !== id);
        localStorage.setItem('cinespur_watchlist', JSON.stringify(updated));
        return updated;
      });
    }
  }, [user]);

  const isInWatchlist = useCallback((id) => watchlist.some(m => String(m.id) === String(id)), [watchlist]);

  return (
    <MovieContext.Provider value={{
      watchlist, addToWatchlist, removeFromWatchlist, isInWatchlist,
      searchQuery, setSearchQuery, selectedGenre, setSelectedGenre,
    }}>
      {children}
    </MovieContext.Provider>
  );
};

export const useMovieContext = () => useContext(MovieContext);
